import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  pending_scan: 'En attente scan',
  validated: 'Validée',
  cancelled: 'Annulée',
  rejected: 'Rejetée',
};

const STATUS_COLORS = {
  validated: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  pending_scan: 'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700',
};

const DischargeRecap = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', region: '' });
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    api.get('/api/distributors/regions')
      .then(res => setRegions(res.data.data || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/api/force-de-vente/discharge-recap?${params}`);
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  const LIMIT = 20;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Récapitulatif des Décharges</h1>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Du</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Au</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Région</label>
            <select
              name="region"
              value={filters.region}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Toutes régions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Statut</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Tous statuts</option>
              <option value="pending_scan">En attente scan</option>
              <option value="validated">Validée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-800 text-white">
            <tr>
              {['N° Décharge', 'Client', 'Dépositaire', 'Région', 'Animateur', 'Chef de Zone', 'Produits', 'Qté', 'Statut', 'Date'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={10} className="text-center py-8 text-gray-400">Chargement...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-gray-400">Aucune décharge</td></tr>
            ) : data.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-xs">{row.dischargeNumber || '—'}</td>
                <td className="px-4 py-2">{row.clientName}</td>
                <td className="px-4 py-2">{row.distributorName || '—'}</td>
                <td className="px-4 py-2">{row.region || '—'}</td>
                <td className="px-4 py-2">{row.animatorName || '—'}</td>
                <td className="px-4 py-2">{row.chefDeZoneName || '—'}</td>
                <td className="px-4 py-2 max-w-xs truncate">{(row.products || []).join(', ') || '—'}</td>
                <td className="px-4 py-2 text-center">{row.totalQuantity || 0}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_LABELS[row.status] || row.status}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.validationDate ? new Date(row.validationDate).toLocaleDateString('fr-FR') : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between items-center px-4 py-3 border-t text-sm">
          <span className="text-gray-500">Total: {total}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Précédent
            </button>
            <span className="px-3 py-1">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * LIMIT >= total}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DischargeRecap;
